import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  InputNumber,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Input,
  Tooltip,
  message,
} from "antd";
import { ContrastTag } from "../../../../../../components/shared";
import {
  BranchesOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  ProtocolStep,
  ActionConfig,
  ActionType,
  ActionTypeDefinition,
  OperatorDefinition,
} from "../../../../../../types/lab/ProtocolTypes";
import {
  fetchAvailableActionTypes,
  fetchAvailableOperators,
  fetchActionTypeOptions,
  testAction,
  validateActionConfig,
} from "../../../../../../api/labiq/ProtocolConditionalLogicManagementApi";
import { fetchTeamMembers } from "../../../../../../api/admin/teamApi";
import { fetchTemplates } from "../../../../../../api/communications/templateApi";

const { TextArea } = Input;

const HTML_FIELD_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

interface OptionItem {
  label: string;
  value: string | number;
}

interface EditingAction extends Partial<ActionConfig> {
  index?: number;
}

export interface ActionNodeEditorProps {
  step: Partial<ProtocolStep>;
  onStepChange: (step: Partial<ProtocolStep>) => void;
  disabled?: boolean;
  companyId?: number;
  companyName?: string;
  /**
   * When true (automated node), shows only the single Automation Actions
   * builder — no completion/failure split, no timeout card, no conditions.
   */
  actionsOnly?: boolean;
}

const ActionNodeEditor: React.FC<ActionNodeEditorProps> = ({
  step,
  onStepChange,
  disabled = false,
  companyId,
  companyName,
  actionsOnly = false,
}) => {
  const [actionTypes, setActionTypes] = useState<Record<string, ActionTypeDefinition>>({});
  const [operators, setOperators] = useState<Record<string, OperatorDefinition>>({});
  const [loading, setLoading] = useState(false);

  // ── Action editor drawer state ──────────────────────────────────────────────
  const [editingAction, setEditingAction] = useState<EditingAction | null>(null);
  const [actionDrawerVisible, setActionDrawerVisible] = useState(false);
  const [actionContext, setActionContext] = useState<"completion" | "failure" | "timeout" | "automation">("completion");
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

  // Cache of dynamically-loaded option lists keyed by source name
  const [optionsCache, setOptionsCache] = useState<Record<string, OptionItem[]>>({});
  const [optionsLoading, setOptionsLoading] = useState<Record<string, boolean>>({});
  const [sourceFullData, setSourceFullData] = useState<Record<string, any[]>>({});

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAvailableActionTypes(), fetchAvailableOperators()])
      .then(([actionTypesData, operatorsData]) => {
        setActionTypes(actionTypesData);
        setOperators(operatorsData);
      })
      .catch((err) => console.error("Failed to load action types:", err))
      .finally(() => setLoading(false));
  }, []);

  // ── Step field updaters ─────────────────────────────────────────────────────

  const updateStepField = (field: string, value: any) => {
    onStepChange({ ...step, [field]: value });
  };

  const updateConfigField = (field: string, value: any) => {
    onStepChange({
      ...step,
      configuration: { ...(step.configuration ?? {}), [field]: value },
    });
  };

  // ── Action list handlers ────────────────────────────────────────────────────

  const openAddDrawer = (ctx: typeof actionContext) => {
    setActionContext(ctx);
    setEditingAction({ type: "update_sample_status" as ActionType, config: {}, critical: false });
    setActionDrawerVisible(true);
    setValidationErrors({});
    setTestResult(null);
  };

  const openEditDrawer = (action: ActionConfig, index: number, ctx: typeof actionContext) => {
    setActionContext(ctx);
    setEditingAction({ ...action, index });
    setActionDrawerVisible(true);
    setValidationErrors({});
    setTestResult(null);
  };

  const deleteAction = (index: number, ctx: typeof actionContext) => {
    const list = getActionList(ctx);
    const updated = list.filter((_, i) => i !== index);
    setActionList(ctx, updated);
    message.success("Action deleted");
  };

  const getActionList = (ctx: typeof actionContext): ActionConfig[] => {
    switch (ctx) {
      case "completion": return step.completion_actions ?? [];
      case "failure":    return step.failure_actions ?? [];
      case "timeout":    return step.timeout_actions ?? [];
      case "automation": return step.configuration?.automation_actions ?? [];
    }
  };

  const setActionList = (ctx: typeof actionContext, actions: ActionConfig[]) => {
    switch (ctx) {
      case "completion": updateStepField("completion_actions", actions); break;
      case "failure":    updateStepField("failure_actions", actions); break;
      case "timeout":    updateStepField("timeout_actions", actions); break;
      case "automation": updateConfigField("automation_actions", actions); break;
    }
  };

  const handleSaveAction = () => {
    if (!editingAction?.type) {
      message.error("Action type is required");
      return;
    }
    const typeDef = actionTypes[editingAction.type];
    if (typeDef?.available === false) {
      message.warning(`"${typeDef.name}" is not yet implemented and cannot be saved.`);
      return;
    }

    const list = [...getActionList(actionContext)];
    const toSave: ActionConfig = {
      type: editingAction.type,
      config: editingAction.config ?? {},
      critical: editingAction.critical ?? false,
    };

    if (editingAction.index !== undefined) {
      list[editingAction.index] = toSave;
    } else {
      list.push(toSave);
    }

    setActionList(actionContext, list);
    setActionDrawerVisible(false);
    setEditingAction(null);
    message.success("Action saved");
  };

  const handleTestAction = async () => {
    if (!editingAction?.type) return;
    setTestLoading(true);
    try {
      const result = await testAction({
        action: { type: editingAction.type, config: editingAction.config ?? {}, critical: editingAction.critical ?? false },
        context_data: {},
        dry_run: true,
      });
      setTestResult(result);
    } catch {
      setTestResult({ message: "Test failed", error: true });
    } finally {
      setTestLoading(false);
    }
  };

  const handleValidateAction = async () => {
    if (!editingAction?.type) return;
    try {
      const result = await validateActionConfig({
        action_type: editingAction.type,
        config: editingAction.config ?? {},
      });
      setValidationErrors(result.errors);
    } catch (err) {
      console.error("Failed to validate action:", err);
    }
  };

  const updateActionProperty = (property: string, value: any) => {
    setEditingAction((prev) => ({ ...prev, [property]: value }));
  };

  const updateActionConfig = (configKey: string, value: any) => {
    setEditingAction((prev) => ({
      ...prev,
      config: { ...prev?.config, [configKey]: value },
    }));
  };

  // ── Dynamic options loader ──────────────────────────────────────────────────

  const loadOptions = useCallback(
    async (source: string) => {
      if (optionsCache[source] || optionsLoading[source]) return;
      setOptionsLoading((prev) => ({ ...prev, [source]: true }));
      try {
        let items: OptionItem[] = [];
        if (source === "users") {
          if (companyName) {
            const result = await fetchTeamMembers(1, 100);
            // labiq stores action users as legacy integer ids, so a staff member without one cannot be picked.
            items = result.data
              .filter((m: any) => m.legacy_user_id != null)
              .map((m: any) => ({
                label: `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || m.email || String(m.legacy_user_id),
                value: m.legacy_user_id,
              }));
          }
        } else if (source === "templates") {
          const result = await fetchTemplates();
          const templates = result.data.filter((t: any) => t.is_active);
          setSourceFullData((prev) => ({ ...prev, templates }));
          items = templates.map((t: any) => ({
            label: `[${t.type.toUpperCase()}] ${t.name}`,
            value: t.id,
          }));
        } else if (companyId) {
          items = await fetchActionTypeOptions(source, companyId);
        }
        setOptionsCache((prev) => ({ ...prev, [source]: items }));
      } catch (err) {
        console.error(`Failed to load options for source "${source}":`, err);
        setOptionsCache((prev) => ({ ...prev, [source]: [] }));
      } finally {
        setOptionsLoading((prev) => ({ ...prev, [source]: false }));
      }
    },
    [companyId, companyName, optionsCache, optionsLoading],
  );

  // ── Config field renderer ───────────────────────────────────────────────────

  const renderActionConfigField = (key: string, fieldDef: any) => {
    const config = editingAction?.config ?? {};
    const value = config[key] ?? fieldDef.default;
    const error = validationErrors[key];
    const fieldLabel = key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    const isRequired = fieldDef.required;

    if (fieldDef.source) {
      const source: string = fieldDef.source;
      if (!optionsCache[source] && !optionsLoading[source]) loadOptions(source);
      const opts = optionsCache[source] ?? [];
      const isLoading = !!optionsLoading[source];
      const isMulti = fieldDef.type === "array";
      const isTemplateField = source === "templates";
      const selectedTemplateId = editingAction?.config?.template_id;
      const selectedTemplate = isTemplateField
        ? (sourceFullData["templates"] ?? []).find((t) => t.id === selectedTemplateId)
        : null;

      const handleSourceChange = (v: any) => {
        if (isTemplateField) {
          const tpl = (sourceFullData["templates"] ?? []).find((t: any) => t.id === v);
          setEditingAction((prev) => ({
            ...prev,
            config: {
              ...prev?.config,
              template_id: v,
              ...(tpl ? { title: tpl.subject || tpl.name, message: tpl.body } : {}),
            },
          }));
        } else {
          updateActionConfig(key, v);
        }
      };

      return (
        <div key={key}>
          <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
            {fieldLabel}
            {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
          </label>
          {fieldDef.description && (
            <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>
              {fieldDef.description}
            </p>
          )}
          <Select
            mode={isMulti ? "multiple" : undefined}
            value={value ?? (isMulti ? [] : undefined)}
            options={opts}
            onChange={handleSourceChange}
            placeholder={isLoading ? "Loading..." : `Select ${fieldLabel}`}
            size="small"
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            disabled={disabled || isLoading}
            loading={isLoading}
            status={error ? "error" : undefined}
          />
          {opts.length === 0 && !isLoading && (
            <div style={{ fontSize: "10px", color: "#faad14", marginTop: 4 }}>
              {source === "users" && !companyName ? "Company context required to load users." : "No options available."}
            </div>
          )}
          {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
        </div>
      );
    }

    switch (fieldDef.type) {
      case "string":
        if (fieldDef.enum) {
          return (
            <div key={key}>
              <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
                {fieldLabel}
                {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
              </label>
              {fieldDef.description && (
                <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
              )}
              <Select
                value={value}
                options={fieldDef.enum.map((opt: string) => ({
                  label: opt.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
                  value: opt,
                }))}
                onChange={(v) => updateActionConfig(key, v)}
                placeholder={`Select ${fieldLabel}`}
                size="small"
                style={{ width: "100%" }}
                disabled={disabled}
                status={error ? "error" : undefined}
              />
              {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
            </div>
          );
        }

        if (key.includes("message") || key.includes("body")) {
          return (
            <div key={key}>
              <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
                {fieldLabel}
                {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
              </label>
              {fieldDef.description && (
                <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
              )}
              <div style={{ border: error ? "1px solid #ff4d4f" : undefined, borderRadius: 6 }}>
                <ReactQuill
                  theme="snow"
                  value={value || ""}
                  onChange={(html) => updateActionConfig(key, html)}
                  modules={HTML_FIELD_QUILL_MODULES}
                  readOnly={disabled}
                  style={{ minHeight: 160 }}
                />
              </div>
              {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
            </div>
          );
        }

        if (key.includes("description") || key.includes("notes")) {
          return (
            <div key={key}>
              <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
                {fieldLabel}
                {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
              </label>
              {fieldDef.description && (
                <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
              )}
              <TextArea
                value={value || ""}
                onChange={(e) => updateActionConfig(key, e.target.value)}
                placeholder={fieldDef.description || `Enter ${fieldLabel.toLowerCase()}`}
                disabled={disabled}
                rows={3}
                size="small"
                status={error ? "error" : undefined}
              />
              {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
            </div>
          );
        }

        return (
          <div key={key}>
            <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
              {fieldLabel}
              {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
            </label>
            {fieldDef.description && (
              <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
            )}
            <Input
              value={value || ""}
              onChange={(e) => updateActionConfig(key, e.target.value)}
              placeholder={fieldDef.description || `Enter ${fieldLabel.toLowerCase()}`}
              disabled={disabled}
              size="small"
              status={error ? "error" : undefined}
            />
            {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
          </div>
        );

      case "number":
        return (
          <div key={key}>
            <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
              {fieldLabel}
              {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
            </label>
            {fieldDef.description && (
              <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
            )}
            <InputNumber
              value={value ?? null}
              onChange={(v) => updateActionConfig(key, v)}
              disabled={disabled}
              size="small"
              style={{ width: "100%" }}
              min={key.includes("priority") ? 1 : undefined}
              max={key.includes("priority") ? 5 : undefined}
              status={error ? "error" : undefined}
            />
            {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
          </div>
        );

      case "boolean":
        return (
          <div key={key} style={{ marginTop: 12 }}>
            <Checkbox
              checked={value ?? fieldDef.default ?? false}
              onChange={(e) => updateActionConfig(key, e.target.checked)}
              disabled={disabled}
            >
              <span style={{ fontSize: "11px", fontWeight: 500 }}>{fieldLabel}</span>
            </Checkbox>
            {fieldDef.description && (
              <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "4px 0 0 24px" }}>{fieldDef.description}</p>
            )}
            {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginLeft: 24, marginTop: 4 }}>{error.join(", ")}</div>}
          </div>
        );

      case "array": {
        const arrayValue = Array.isArray(value) ? value : [];
        return (
          <div key={key}>
            <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
              {fieldLabel}
              {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
            </label>
            {fieldDef.description && (
              <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
            )}
            <Select
              mode="tags"
              value={arrayValue}
              onChange={(v) => updateActionConfig(key, v ?? [])}
              placeholder="Enter values and press Enter"
              disabled={disabled}
              size="small"
              style={{ width: "100%" }}
              status={error ? "error" : undefined}
            />
            <div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Press Enter after each value</div>
            {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
          </div>
        );
      }

      default:
        if (typeof value === "object" && value !== null) {
          return (
            <div key={key}>
              <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
                {fieldLabel}
                {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
              </label>
              {fieldDef.description && (
                <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
              )}
              <div style={{ padding: 8, border: "1px solid #ffe58f", borderRadius: 4 }}>
                <p style={{ fontSize: "10px", color: "#ad6800", margin: 0 }}>
                  Complex object configuration — contact administrator for advanced setup
                </p>
              </div>
              {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
            </div>
          );
        }
        return (
          <div key={key}>
            <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>
              {fieldLabel}
              {isRequired && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
            </label>
            {fieldDef.description && (
              <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "0 0 8px 0" }}>{fieldDef.description}</p>
            )}
            <Input
              value={value?.toString() || ""}
              onChange={(e) => updateActionConfig(key, e.target.value)}
              disabled={disabled}
              size="small"
              status={error ? "error" : undefined}
            />
            {error && <div style={{ color: "#ff4d4f", fontSize: "10px", marginTop: 4 }}>{error.join(", ")}</div>}
          </div>
        );
    }
  };

  const renderActionConfigForm = (actionType: string) => {
    const typeDef = actionTypes[actionType];
    if (!typeDef) return null;
    const schema = typeDef.config_schema;
    if (!schema || Object.keys(schema).length === 0) {
      return <Alert message="This action type doesn't require additional configuration" type="info" showIcon style={{ fontSize: "11px" }} />;
    }
    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {Object.entries(schema).map(([key, fieldDef]) => renderActionConfigField(key, fieldDef))}
      </Space>
    );
  };

  // ── Action table ────────────────────────────────────────────────────────────

  const buildColumns = (ctx: typeof actionContext) => [
    {
      title: "Action",
      key: "type",
      width: 140,
      render: (record: ActionConfig) => {
        const typeDef = actionTypes[record.type];
        const colorMap: Record<string, string> = {
          update_sample_status: "#1890ff",
          send_notification: "#faad14",
          create_quality_check: "#8c8c8c",
          update_lab_order_status: "#52c41a",
        };
        return (
          <Space direction="vertical" size={4}>
            <ContrastTag background={colorMap[record.type] ?? "#1890ff"} style={{ fontSize: "10px" }}>
              {typeDef?.name || record.type}
            </ContrastTag>
            {record.critical && (
              <Badge count="Critical" style={{ backgroundColor: "#ff4d4f", fontSize: "10px" }} />
            )}
          </Space>
        );
      },
    },
    {
      title: "Configuration",
      key: "config",
      render: (record: ActionConfig) => {
        const keys = Object.keys(record.config ?? {});
        if (!keys.length) return <span style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>No config</span>;
        return (
          <Space size={4} wrap>
            {keys.slice(0, 3).map((k, i) => {
              const v = record.config[k];
              const display = typeof v === "string" ? (v.length > 15 ? `${v.slice(0, 15)}…` : v) : JSON.stringify(v).slice(0, 15);
              return <Tag key={i} style={{ fontSize: "10px" }}>{k}: {display}</Tag>;
            })}
            {keys.length > 3 && <Badge count={`+${keys.length - 3}`} style={{ backgroundColor: "#8c8c8c", fontSize: "10px" }} />}
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: any, record: ActionConfig, index: number) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditDrawer(record, index, ctx)} disabled={disabled} />
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => deleteAction(index, ctx)} disabled={disabled} />
        </Space>
      ),
    },
  ];

  // ── Reusable action section ─────────────────────────────────────────────────

  const renderActionSection = (
    ctx: typeof actionContext,
    actions: ActionConfig[],
    icon: React.ReactNode,
    label: string,
    description: string,
  ) => (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <Space size={6}>
          {icon}
          <span style={{ fontSize: "11px", fontWeight: 500 }}>{label}</span>
          {actions.length > 0 && <Badge count={actions.length} />}
        </Space>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openAddDrawer(ctx)} disabled={disabled}>
          Add Action
        </Button>
      </div>
      <p style={{ fontSize: "10px", color: "var(--mg-text-secondary)", marginBottom: 8 }}>{description}</p>
      {actions.length > 0 ? (
        <Table columns={buildColumns(ctx)} dataSource={actions} pagination={false} size="small" rowKey={(_, i) => i!} />
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "20px 16px",
            border: "2px dashed var(--border-color)",
            borderRadius: 8,
          }}
        >
          {icon}
          <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: "8px 0 0" }}>No {label.toLowerCase()} configured</p>
        </div>
      )}
    </div>
  );

  const actionTypeOptions = Object.entries(actionTypes).map(([key, def]) => ({
    label: def.available === false ? `${def.name} (Coming Soon)` : def.name,
    value: key,
    disabled: def.available === false,
  }));

  // ── Loading state ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <Space size="middle">
          <Spin />
          <span style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>Loading action types…</span>
        </Space>
      </div>
    );
  }

  // ── Automated node: single automation actions list ──────────────────────────

  if (actionsOnly) {
    const automationActions = step.configuration?.automation_actions ?? [];
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SettingOutlined style={{ fontSize: 12 }} />
            <h6 style={{ margin: 0, fontSize: 12, fontWeight: 500 }}>Automation Actions</h6>
            {automationActions.length > 0 && (
              <Badge
                count={`${automationActions.length} action${automationActions.length !== 1 ? "s" : ""}`}
                style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }}
              />
            )}
          </div>
          <div style={{ fontSize: "10px", color: "var(--mg-text-secondary)" }}>Executed when this node is reached</div>
        </div>
        <p style={{ fontSize: "11px", color: "var(--mg-text-secondary)", marginBottom: 12 }}>
          These actions fire automatically when the protocol reaches this node. Use conditional nodes to branch based on results.
        </p>
        {renderActionSection(
          "automation",
          automationActions,
          <SettingOutlined style={{ fontSize: 11, color: "var(--mg-primary)" }} />,
          "Automation Actions",
          "Triggered automatically when this node is reached.",
        )}
      </div>
    );
  }

  // ── Interactive step: completion / failure / timeout sections ───────────────

  const completionActions = step.completion_actions ?? [];
  const failureActions    = step.failure_actions ?? [];
  const timeoutActions    = step.timeout_actions ?? [];
  const totalActions      = completionActions.length + failureActions.length + timeoutActions.length;
  const hasTimeout        = !!step.timeout_minutes;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <Space size={6}>
          <BranchesOutlined style={{ fontSize: 12 }} />
          <h6 style={{ margin: 0, fontSize: 12, fontWeight: 500 }}>Conditional Logic</h6>
          <Space size={4}>
            {totalActions > 0 && <Badge count={`${totalActions} actions`} style={{ backgroundColor: "#52c41a", fontSize: "10px" }} />}
            {hasTimeout && (
              <Tag color="orange" style={{ fontSize: "10px" }}>
                {step.timeout_minutes}m timeout
              </Tag>
            )}
          </Space>
        </Space>
        <div style={{ fontSize: "10px", color: "var(--text-color-secondary)" }}>Configure automated behaviours</div>
      </div>

      {/* Timeout setting */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <label style={{ fontSize: "11px", fontWeight: 500 }}>Step Timeout:</label>
          <InputNumber
            value={step.timeout_minutes ?? null}
            onChange={(v) => updateStepField("timeout_minutes", v)}
            disabled={disabled}
            placeholder="No timeout"
            size="small"
            style={{ width: 120 }}
            min={1}
            max={10080}
            addonAfter="min"
          />
        </div>
        <p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: 0 }}>Automatic timeout duration</p>
      </Card>

      {/* Action sections */}
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {renderActionSection(
          "completion",
          completionActions,
          <CheckCircleOutlined style={{ fontSize: 11, color: "#52c41a" }} />,
          "On Completion",
          "Actions when step completes successfully.",
        )}

        <div style={{ height: 1, backgroundColor: "var(--border-color)" }} />

        {renderActionSection(
          "failure",
          failureActions,
          <CloseCircleOutlined style={{ fontSize: 11, color: "#ff4d4f" }} />,
          "On Failure",
          "Actions when step fails or has errors.",
        )}

        {step.timeout_minutes && (
          <>
            <div style={{ height: 1, backgroundColor: "var(--border-color)" }} />
            {renderActionSection(
              "timeout",
              timeoutActions,
              <ClockCircleOutlined style={{ fontSize: 11, color: "#faad14" }} />,
              "On Timeout",
              `Actions when step exceeds ${step.timeout_minutes} minute timeout.`,
            )}
          </>
        )}
      </Space>

      {/* Action editor drawer */}
      <Drawer
        title={
          <Space size={8}>
            {actionContext === "completion" && <CheckCircleOutlined />}
            {actionContext === "failure" && <CloseCircleOutlined />}
            {actionContext === "timeout" && <ClockCircleOutlined />}
            {actionContext === "automation" && <SettingOutlined />}
            <span>{editingAction?.index !== undefined ? "Edit Action" : "Add Action"}</span>
          </Space>
        }
        open={actionDrawerVisible}
        onClose={() => setActionDrawerVisible(false)}
        width={600}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Space size="small">
              {editingAction?.type && (
                <>
                  <Button size="small" icon={<CheckCircleOutlined />} onClick={handleValidateAction}>Validate</Button>
                  <Button size="small" icon={<PlayCircleOutlined />} onClick={handleTestAction} loading={testLoading}>Test</Button>
                </>
              )}
            </Space>
            <Space size="small">
              <Button icon={<CloseOutlined />} onClick={() => setActionDrawerVisible(false)} size="small">Cancel</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveAction}
                disabled={!editingAction?.type || Object.keys(validationErrors).length > 0}
                size="small"
              >
                Save Action
              </Button>
            </Space>
          </div>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div>
              <label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Action Type *</label>
              <Select
                value={editingAction?.type}
                options={actionTypeOptions}
                onChange={(v) => updateActionProperty("type", v)}
                placeholder="Select action type"
                size="small"
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="label"
              />
              {editingAction?.type && actionTypes[editingAction.type] && (
                <div
                  style={{
                    fontSize: "10px",
                    color: actionTypes[editingAction.type].available === false ? "#faad14" : "var(--text-color-secondary)",
                    marginTop: 4,
                  }}
                >
                  {actionTypes[editingAction.type].available === false && <LockOutlined style={{ marginRight: 4 }} />}
                  {actionTypes[editingAction.type].description}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <Checkbox
                checked={editingAction?.critical ?? false}
                onChange={(e) => updateActionProperty("critical", e.target.checked)}
              >
                <span style={{ fontSize: "11px", fontWeight: 500 }}>Critical Action</span>
              </Checkbox>
            </div>
          </div>

          {editingAction?.type && actionTypes[editingAction.type] && (
            <div>
              <h6 style={{ fontSize: "12px", fontWeight: 500, marginBottom: 12 }}>Configuration</h6>
              {renderActionConfigForm(editingAction.type)}
            </div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <Alert
              message="Validation Errors"
              description={
                <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                  {Object.entries(validationErrors).map(([field, errors]) => (
                    <li key={field} style={{ fontSize: "10px" }}>
                      <strong>{field}:</strong> {errors.join(", ")}
                    </li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
            />
          )}

          {testResult && (
            <Alert
              message="Test Result"
              description={
                <div>
                  <p style={{ fontSize: "10px", margin: 0 }}>{testResult.message}</p>
                  {testResult.preview && (
                    <div style={{ marginTop: 8 }}>
                      <strong style={{ fontSize: "10px" }}>Action Preview:</strong>
                      <div
                        style={{
                          padding: 8,
                          backgroundColor: "var(--background-color)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 4,
                          marginTop: 4,
                        }}
                      >
                        {Object.entries(testResult.preview).map(([k, v]) => (
                          <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: "10px" }}>
                            <span style={{ color: "var(--text-color-secondary)" }}>{k}:</span>
                            <span style={{ fontWeight: 500 }}>{JSON.stringify(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              }
              type={testResult.error ? "error" : "success"}
              showIcon
            />
          )}
        </Space>
      </Drawer>
    </div>
  );
};

export default ActionNodeEditor;
